import React, { useEffect, useRef, useState, useCallback } from "react";
import * as powerbi from "powerbi-client";
import LoginButton from "./LoginButton";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest } from "./authConfig";

const PowerBIReportEmbedded = ({ reportId, groupId}) => {

    const reportRef = useRef(null);
    const reportInstance = useRef(null);
    const powerbiService = useRef(null);
    const retryEmbed = useRef(false);

    const { instance } = useMsal();

    const [loading, setLoading] = useState(false);
    //const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [hasWorkspaceAccess, setHasWorkspaceAccess] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    const { accounts } = useMsal();

    const isAuthenticated = accounts.length > 0;

    //-----------------------------------------------------
    // Crear servicio Power BI una sola vez
    //-----------------------------------------------------

    useEffect(() => {

        powerbiService.current = new powerbi.service.Service(
            powerbi.factories.hpmFactory,
            powerbi.factories.wpmpFactory,
            powerbi.factories.routerFactory
        );

        return () => {

            if (reportRef.current) {

                powerbiService.current.reset(reportRef.current);

            }

        };

    }, []);

    //-----------------------------------------------------
// Obtener cuenta activa
//-----------------------------------------------------

const getAccount = () => {

    let account = instance.getActiveAccount();

    if (!account) {

        const accounts = instance.getAllAccounts();

        if (accounts.length > 0) {

            account = accounts[0];
            instance.setActiveAccount(account);

        }

    }

    return account;

};

//-----------------------------------------------------
// Obtener token
//-----------------------------------------------------

const tokenPromise = useRef(null);

const cachedToken = useRef(null);

const expiresOn = useRef(null);

const getPowerBIToken = useCallback(async (forceRefresh = false) => {

    //---------------------------------
    // Token válido
    //---------------------------------

    if (

        !forceRefresh &&
        cachedToken.current &&
        expiresOn.current &&
        expiresOn.current > Date.now() + 300000

    ) {

        return cachedToken.current;

    }

    //---------------------------------
    // Ya existe una petición
    //---------------------------------

    if (tokenPromise.current) {

        return tokenPromise.current;

    }

    tokenPromise.current = (async () => {

        let account = getAccount();

        if (!account) {

            const login = await instance.loginPopup(loginRequest);

            account = login.account;

            instance.setActiveAccount(account);

        }

        try {

            const response = await instance.acquireTokenSilent({

                account,

                scopes: [

                    "https://analysis.windows.net/powerbi/api/.default"

                ],

                forceRefresh

            });

            cachedToken.current = response.accessToken;

            expiresOn.current = response.expiresOn.getTime();

            return response.accessToken;

        }
        catch (error) {

            console.error(error);

            const response = await instance.acquireTokenPopup({

                account,

                scopes: [

                    "https://analysis.windows.net/powerbi/api/.default"

                ]

            });

            cachedToken.current = response.accessToken;

            expiresOn.current = Date.now() + 3600000;

            return response.accessToken;

        }

    })();

    try {

        return await tokenPromise.current;

    }
    finally {

        tokenPromise.current = null;

    }

}, [instance]);

//-----------------------------------------------------
// Limpiar Reporte
//-----------------------------------------------------

const resetReport = () => {

    try {

        if (
            reportRef.current &&
            powerbiService.current
        ) {

            powerbiService.current.reset(reportRef.current);

        }

    }
    catch (e) {

        console.log(e);

    }

};


//-----------------------------------------------------
// Llamada genérica al API de Power BI
//-----------------------------------------------------

const callPowerBIApi = useCallback(async (url) => {

    let retry = false;

    while (true) {

        try {

            const token = await getPowerBIToken(retry);

            const response = await fetch(url, {

                method: "GET",

                headers: {

                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",

                }

            });

            // Token expirado o sesión inválida
            if (
                (response.status === 401 || response.status === 403)
                && !retry
            ) {

                console.log("Renovando token...");

                retry = true;

                continue;

            }

            return response;

        }
        catch (error) {

            if (!retry) {

                retry = true;

                continue;

            }

            throw error;

        }

    }

}, [getPowerBIToken]);

//-----------------------------------------------------
// Validar acceso al Workspace
//-----------------------------------------------------

const validateWorkspace = useCallback(async () => {

    try {

        const response = await callPowerBIApi(

            `https://api.powerbi.com/v1.0/myorg/groups/${groupId}`

        );

        if (response.ok) {

            return {
                success: true
            };

        }

        if (response.status === 404) {

            return {

                success: false,
                message: "El Workspace no existe."

            };

        }

        if (response.status === 403) {

            return {

                success: false,
                message: "No tiene permisos sobre el Workspace."

            };

        }

        return {

            success: false,
            message: "No fue posible validar el Workspace."

        };

    }
    catch (error) {

        console.error(error);

        return {

            success: false,
            message: `Error consultando el Workspace: ${error.message || "Ocurrió un error inesperado."}`

        };

    }

}, [groupId, callPowerBIApi]);

//-----------------------------------------------------
// Validar Reporte
//-----------------------------------------------------

const validateReport = useCallback(async () => {

    try {

        const response = await callPowerBIApi(

            `https://api.powerbi.com/v1.0/myorg/groups/${groupId}/reports/${reportId}`

        );

        if (response.ok) {

            return {

                success: true

            };

        }

        if (response.status === 404) {

            return {

                success: false,
                message: "El reporte no existe."

            };

        }

        if (response.status === 403) {

            return {

                success: false,
                message: "No tiene permisos sobre el reporte."

            };

        }

        return {

            success: false,
            message: "No fue posible validar el reporte."

        };

    }
    catch (error) {

        console.error(error);

        return {

            success: false,
            message: "Error consultando el reporte."

        };

    }

}, [groupId, reportId, callPowerBIApi]);

//-----------------------------------------------------
// Validaciones previas al Embed
//-----------------------------------------------------

const validateAccess = useCallback(async () => {

    setErrorMessage("");
    setHasWorkspaceAccess(null);

    //----------------------------------------
    // Workspace
    //----------------------------------------

    const workspace = await validateWorkspace();

    if (!workspace.success) {

        setHasWorkspaceAccess(false);

        setErrorMessage(workspace.message);

        return false;

    }

    //----------------------------------------
    // Reporte
    //----------------------------------------

    const report = await validateReport();

    if (!report.success) {

        setHasWorkspaceAccess(false);

        setErrorMessage(report.message);

        return false;

    }

    setHasWorkspaceAccess(true);

    return true;

}, [validateWorkspace, validateReport]);


//-----------------------------------------------------
// Embed Report
//-----------------------------------------------------

const embedReport = useCallback(async (retry = false) => {

    try {

        setLoading(true);

        resetReport();

        const token = await getPowerBIToken(retry);

                if (!reportRef.current) {

            console.log("Esperando render...");

            await new Promise(resolve => setTimeout(resolve, 100));

        }

        if (!reportRef.current) {

            console.log("No existe el div.");

            return;

        }

        const config = {

            type: "report",

            id: reportId,

            embedUrl: `https://app.powerbi.com/reportEmbed?reportId=${reportId}&groupId=${groupId}`,

            accessToken: token,

            tokenType: powerbi.models.TokenType.Aad,

            permissions: powerbi.models.Permissions.All,

            settings: {

                panes: {

                    filters: {

                        visible: false,

                    },

                    pageNavigation: {

                        visible: true,

                    }

                },

                layoutType: powerbi.models.LayoutType.FitToWidth

            }

        };

        console.log("reportRef.current:", reportRef.current);
        console.log("powerbiService.current:", powerbiService.current);
        console.log("reportId:", reportId);
        console.log("groupId:", groupId);
        console.log("token:", token ? "OK" : "NULL");

        if (!reportRef.current) {

            console.log("Esperando render...");

            await new Promise(resolve => setTimeout(resolve, 100));

        }

        if (!reportRef.current) {

            console.log("No existe el div.");

            return;

        }

        reportInstance.current = powerbiService.current.embed(
            reportRef.current,
            config
        );

        registerEvents();

    }
    catch (error) {

        console.error(error);

        setLoading(false);

        setHasWorkspaceAccess(false);

        setErrorMessage(
            "Ocurrió un error al cargar el reporte."
        );

    }

}, [groupId, reportId, getPowerBIToken]);

//-----------------------------------------------------
// Eventos del Reporte
//-----------------------------------------------------

const registerEvents = () => {

    if (!reportInstance.current) return;

    reportInstance.current.off("loaded");
    reportInstance.current.off("rendered");
    reportInstance.current.off("error");

    //------------------------------------------------

    reportInstance.current.on("loaded", () => {

        console.log("Reporte cargado");

    });

    //------------------------------------------------

    reportInstance.current.on("rendered", () => {

        console.log("Reporte renderizado");

        retryEmbed.current = false;

        setLoading(false);

    });

    //------------------------------------------------

    reportInstance.current.on("error", async (event) => {

        console.error(event.detail);

        //------------------------------------------------
        // Si ya reintentamos una vez
        //------------------------------------------------

        if (retryEmbed.current) {

            //resetReport();

            setLoading(false);

            setHasWorkspaceAccess(false);

            setErrorMessage(
                "No fue posible visualizar el reporte."
            );

            return;

        }

        //------------------------------------------------
        // Reintentamos una sola vez
        //------------------------------------------------

        retryEmbed.current = true;

        console.log("Reintentando Embed...");

        try {

            await embedReport(true);

        }
        catch {

            //resetReport();

            setLoading(false);

            setHasWorkspaceAccess(false);

            setErrorMessage(
                "No fue posible visualizar el reporte."
            );

        }

    });

};

//-----------------------------------------------------
// Cargar Reporte
//-----------------------------------------------------

const loadReport = useCallback(async () => {

    if (!reportId || !groupId)
        return;

    setLoading(true);

    retryEmbed.current = false;

    resetReport();

    const access = await validateAccess();

    if (!access) {

        setLoading(false);

        return;

    }

    setHasWorkspaceAccess(true);

    await embedReport();



}, [

    reportId,

    groupId,

    validateAccess,

    embedReport

]);

//-----------------------------------------------------
// Cambio de Reporte
//-----------------------------------------------------

useEffect(() => {

    loadReport();

    return () => {

        resetReport();

    };

}, [loadReport]);

    useEffect(() => {

        if (!isAuthenticated)
            return;

        if (!reportRef.current)
            return;

        loadReport();

    }, [

        isAuthenticated,

        reportId,

        groupId

    ]);

//-----------------------------------------------------
// Sin reporte seleccionado
//-----------------------------------------------------

if (!reportId || !groupId) {

    return (

        <div
            style={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }}
        >

            Seleccione un reporte.

        </div>

    );

}

//-----------------------------------------------------
// Usuario no autenticado
//-----------------------------------------------------

if (!isAuthenticated) {

    return (

        <div
            style={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }}
        >

            <LoginButton />

        </div>

    );

}

//-----------------------------------------------------
// Sin acceso
//-----------------------------------------------------

if (hasWorkspaceAccess === false) {

    return (

        <div
            style={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                padding: 20,
                color: "#c62828",
                textAlign: "center"
            }}
        >
            {errorMessage} <br></br><a>Si necesitas acceso, comunícate con el administrador del sitio o solicita la autorización al Data Owner del área de trabajo. Conoce al Data Owner aquí: </a> <a target="_blank"
                rel="noopener noreferrer" href="https://grupocentenario.sharepoint.com/:fl:/g/contentstorage/CSP_e2750bc5-419c-4700-9179-6714e0a317bc/IQCf7RCtqZ0uSbKVAI6CVC7sAbK44kqPXwrFcKi5D4KP4HA?e=xPcW4f&nav=cz0lMkZjb250ZW50c3RvcmFnZSUyRkNTUF9lMjc1MGJjNS00MTljLTQ3MDAtOTE3OS02NzE0ZTBhMzE3YmMmZD1iJTIxeFF0MTRweEJBRWVSZVdjVTRLTVh2RzFtdENlWjJFTkpuV2l4dkl1NGxVNGZESndjcFB6dlRwekJLbGJBTGVGTSZmPTAxU0RLTDc1TTc1VUlLM0tNNUZaRTNGRklBUjJCRklMWE0mYz0lMkYmYT1Mb29wQXBwJnA9JTQwZmx1aWR4JTJGbG9vcC1wYWdlLWNvbnRhaW5lciZ4PSU3QiUyMnclMjIlM0ElMjJUMFJUVUh4bmNuVndiMk5sYm5SbGJtRnlhVzh1YzJoaGNtVndiMmx1ZEM1amIyMThZaUY0VVhReE5IQjRRa0ZGWlZKbFYyTlZORXROV0haSE1XMTBRMlZhTWtWT1NtNVhhWGgyU1hVMGJGVTBaa1JLZDJOd1VIcDJWSEI2UWt0c1lrRk1aVVpOZkRBeFUwUkxURGMxU2taSFRGRTJNMEZQVkVWR1JrdzFTRlkyVmxSV05FODBXVFElM0QlMjIlMkMlMjJpJTIyJTNBJTIyMWNlZGY2MGYtMGExNi00NTQ4LTlkYmUtY2EyZjlhY2M4ODRjJTIyJTdE">Aquí</a>

        </div>

    );

}

//-----------------------------------------------------
// Render
//-----------------------------------------------------

return (

    <div
        style={{
            width: "100%",
            height: "100%",
            position: "relative"
        }}
    >

        {
            loading &&

            <div
                style={{

                    position: "absolute",

                    top: 0,

                    left: 0,

                    width: "100%",

                    height: "100%",

                    background: "rgba(255,255,255,.75)",

                    display: "flex",

                    justifyContent: "center",

                    alignItems: "center",

                    zIndex: 1000

                }}
            >

                <div>

                    

                </div>

            </div>

        }

        <div

            ref={reportRef}

            style={{

                width: "100%",

                height: "100%",

                visibility: hasWorkspaceAccess ? "visible" : "hidden"

            }}

        />

    </div>

);

};

export default PowerBIReportEmbedded;